import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  CardActions,
  Typography,
  Button,
  Grid,
  Container,
  Paper,
  Chip,
  Stack,
} from '@mui/material';
import {
  Code as CodeIcon,
  CompareArrows as CompareIcon,
  Psychology as AIIcon,
  QueryStats as QueryIcon,
  Preview as PreviewIcon,
  Speed as SpeedIcon,
  AttachMoney as CostIcon,
  ArrowForward as ArrowIcon,
} from '@mui/icons-material';

const SamplingDemoLanding: React.FC = () => {
  const navigate = useNavigate();

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box sx={{ textAlign: 'center', mb: 6 }}>
        <Typography variant="h3" gutterBottom>
          SERP Keyword Sampling Demos
        </Typography>
        <Typography variant="h6" color="text.secondary" sx={{ mb: 3 }}>
          Explore and test different keyword sampling strategies for cost-effective SERP analysis
        </Typography>
        
        <Paper elevation={0} sx={{ p: 3, bgcolor: 'primary.50', mb: 4 }}>
          <Typography variant="body1" sx={{ mb: 2 }}>
            These demo tools help you understand and optimize keyword sampling strategies for SERP API usage.
            Test different approaches, compare their effectiveness, and find the right balance between coverage and cost.
          </Typography>
          <Stack direction="row" spacing={2} justifyContent="center">
            <Chip icon={<QueryIcon />} label="SQL Query Testing" color="primary" />
            <Chip icon={<AIIcon />} label="AI-Powered Generation" color="secondary" />
            <Chip icon={<CompareIcon />} label="Strategy Comparison" color="success" />
          </Stack>
        </Paper>
      </Box>

      <Grid container spacing={4}>
        {/* Sampling Strategy Demo */}
        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            <CardContent sx={{ flexGrow: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <CodeIcon sx={{ fontSize: 40, mr: 2, color: 'primary.main' }} />
                <Typography variant="h5" component="h2">
                  Sampling Strategy Explorer
                </Typography>
              </Box>
              
              <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                Test and explore different keyword sampling strategies with interactive SQL queries.
              </Typography>
              
              <Typography variant="h6" gutterBottom>
                Features:
              </Typography>
              <ul>
                <li>
                  <Typography variant="body2">
                    <strong>Predefined Strategies:</strong> Test representative, high-volume, stratified, and topic-based sampling
                  </Typography>
                </li>
                <li>
                  <Typography variant="body2">
                    <strong>Custom SQL:</strong> Write and test your own sampling queries
                  </Typography>
                </li>
                <li>
                  <Typography variant="body2">
                    <strong>AI Query Builder:</strong> Generate sampling queries using natural language
                  </Typography>
                </li>
                <li>
                  <Typography variant="body2">
                    <strong>Live Preview:</strong> See sample results and distribution statistics
                  </Typography>
                </li>
              </ul>
              
              <Box sx={{ mt: 3 }}>
                <Stack direction="row" spacing={1}>
                  <Chip size="small" icon={<PreviewIcon />} label="Interactive Preview" />
                  <Chip size="small" icon={<AIIcon />} label="AI-Powered" />
                </Stack>
              </Box>
            </CardContent>
            <CardActions>
              <Button 
                size="large" 
                variant="contained" 
                fullWidth
                endIcon={<ArrowIcon />}
                onClick={() => navigate('/sampling-demo')}
              >
                Launch Strategy Explorer
              </Button>
            </CardActions>
          </Card>
        </Grid>

        {/* Sampling Comparison Demo */}
        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            <CardContent sx={{ flexGrow: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <CompareIcon sx={{ fontSize: 40, mr: 2, color: 'success.main' }} />
                <Typography variant="h5" component="h2">
                  Strategy Comparison Tool
                </Typography>
              </Box>
              
              <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                Compare multiple sampling strategies side-by-side to find the optimal approach.
              </Typography>
              
              <Typography variant="h6" gutterBottom>
                Features:
              </Typography>
              <ul>
                <li>
                  <Typography variant="body2">
                    <strong>Side-by-Side Comparison:</strong> Compare up to 5 strategies simultaneously
                  </Typography>
                </li>
                <li>
                  <Typography variant="body2">
                    <strong>Cost Analysis:</strong> Calculate and compare API costs for each strategy
                  </Typography>
                </li>
                <li>
                  <Typography variant="body2">
                    <strong>Coverage Metrics:</strong> Evaluate keyword coverage and distribution
                  </Typography>
                </li>
                <li>
                  <Typography variant="body2">
                    <strong>Visual Charts:</strong> Interactive charts for performance and distribution analysis
                  </Typography>
                </li>
              </ul>
              
              <Box sx={{ mt: 3 }}>
                <Stack direction="row" spacing={1}>
                  <Chip size="small" icon={<CostIcon />} label="Cost Analysis" />
                  <Chip size="small" icon={<SpeedIcon />} label="Performance Metrics" />
                </Stack>
              </Box>
            </CardContent>
            <CardActions>
              <Button 
                size="large" 
                variant="contained" 
                color="success"
                fullWidth
                endIcon={<ArrowIcon />}
                onClick={() => navigate('/sampling-comparison')}
              >
                Launch Comparison Tool
              </Button>
            </CardActions>
          </Card>
        </Grid>
      </Grid>

      {/* Additional Information */}
      <Paper elevation={0} sx={{ mt: 6, p: 4, bgcolor: 'grey.100' }}>
        <Typography variant="h6" gutterBottom>
          Why Keyword Sampling Matters
        </Typography>
        <Grid container spacing={3}>
          <Grid item xs={12} md={4}>
            <Box sx={{ textAlign: 'center' }}>
              <CostIcon sx={{ fontSize: 48, color: 'primary.main', mb: 1 }} />
              <Typography variant="subtitle1" gutterBottom>
                Cost Optimization
              </Typography>
              <Typography variant="body2" color="text.secondary">
                SERP API calls can be expensive. Smart sampling reduces costs by 50-90% while maintaining insights.
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12} md={4}>
            <Box sx={{ textAlign: 'center' }}>
              <QueryIcon sx={{ fontSize: 48, color: 'success.main', mb: 1 }} />
              <Typography variant="subtitle1" gutterBottom>
                Strategic Coverage
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Different strategies provide different insights. Choose based on your analysis goals.
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12} md={4}>
            <Box sx={{ textAlign: 'center' }}>
              <SpeedIcon sx={{ fontSize: 48, color: 'warning.main', mb: 1 }} />
              <Typography variant="subtitle1" gutterBottom>
                Faster Results
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Smaller, targeted samples process faster and provide quicker insights for decision-making.
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Quick Links */}
      <Box sx={{ mt: 4, textAlign: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          Need help? Check out the{' '}
          <Button size="small" onClick={() => navigate('/docs/serp-sampling')}>
            Documentation
          </Button>
          {' '}or contact the development team.
        </Typography>
      </Box>
    </Container>
  );
};

export default SamplingDemoLanding; 